import React, { useState, useEffect } from 'react';
import authService from '../../services/authService';
import './Admin.css';

const AutoRechargeRules = ({ organizationId, accountId }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Create/Edit Modal
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    accountId: accountId || '',
    ruleName: '',
    triggerType: 'THRESHOLD',
    thresholdAmount: '',
    thresholdPercentage: '',
    rechargeAmount: '',
    maxRechargePerMonth: '',
    scheduleFrequency: '',
    scheduleDay: '',
    paymentMethodId: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Test Modal
  const [showTestModal, setShowTestModal] = useState(false);
  const [testingRule, setTestingRule] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    loadRules();
  }, [organizationId, accountId, statusFilter]);

  const loadRules = async () => {
    setLoading(true);
    setError('');
    try {
      const t = authService.getToken();
      const params = new URLSearchParams();
      
      if (organizationId) params.append('organizationId', organizationId);
      if (accountId) params.append('accountId', accountId);
      if (statusFilter) params.append('status', statusFilter);

      const res = await fetch(`/api/credits/auto-recharge/rules?${params}`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to load auto-recharge rules');
      }

      const data = await res.json();
      setRules(data.rules || []);
    } catch (e) {
      setError(e.message || 'Failed to load auto-recharge rules');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRule = async () => {
    if (!ruleForm.ruleName || !ruleForm.rechargeAmount || parseFloat(ruleForm.rechargeAmount) <= 0) {
      setError('Please fill in all required fields');
      return;
    }

    if (ruleForm.triggerType === 'THRESHOLD' && !ruleForm.thresholdAmount && !ruleForm.thresholdPercentage) {
      setError('Please specify either threshold amount or percentage');
      return;
    }

    if (ruleForm.triggerType === 'SCHEDULED' && !ruleForm.scheduleFrequency) {
      setError('Please specify schedule frequency');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const t = authService.getToken();
      const endpoint = editingRule 
        ? `/api/credits/auto-recharge/rules/${editingRule.ruleId}`
        : '/api/credits/auto-recharge/rules';
      
      const method = editingRule ? 'PATCH' : 'POST';
      
      const body = editingRule ? {
        ruleName: ruleForm.ruleName,
        rechargeAmount: parseFloat(ruleForm.rechargeAmount),
        maxRechargePerMonth: ruleForm.maxRechargePerMonth ? parseFloat(ruleForm.maxRechargePerMonth) : null,
        scheduleFrequency: ruleForm.scheduleFrequency || null,
        scheduleDay: ruleForm.scheduleDay ? parseInt(ruleForm.scheduleDay) : null,
        paymentMethodId: ruleForm.paymentMethodId || null,
        status: ruleForm.status || null
      } : {
        organizationId: organizationId,
        accountId: ruleForm.accountId,
        ruleName: ruleForm.ruleName,
        triggerType: ruleForm.triggerType,
        thresholdAmount: ruleForm.thresholdAmount ? parseFloat(ruleForm.thresholdAmount) : null,
        thresholdPercentage: ruleForm.thresholdPercentage ? parseFloat(ruleForm.thresholdPercentage) : null,
        rechargeAmount: parseFloat(ruleForm.rechargeAmount),
        maxRechargePerMonth: ruleForm.maxRechargePerMonth ? parseFloat(ruleForm.maxRechargePerMonth) : null,
        scheduleFrequency: ruleForm.scheduleFrequency || null,
        scheduleDay: ruleForm.scheduleDay ? parseInt(ruleForm.scheduleDay) : null,
        paymentMethodId: ruleForm.paymentMethodId || null
      };

      const res = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {})
        },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to save rule');
      }

      setShowRuleModal(false);
      setEditingRule(null);
      resetForm();
      loadRules();
    } catch (e) {
      setError(e.message || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this auto-recharge rule?')) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const t = authService.getToken();
      const res = await fetch(`/api/credits/auto-recharge/rules/${ruleId}`, {
        method: 'DELETE',
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to delete rule');
      }

      loadRules();
    } catch (e) {
      setError(e.message || 'Failed to delete rule');
    } finally {
      setLoading(false);
    }
  };

  const handleTestRule = async (rule) => {
    setTestingRule(rule);
    setTestResult(null);
    setShowTestModal(true);
    setTesting(true);
    setError('');
    
    try {
      const t = authService.getToken();
      const res = await fetch('/api/credits/auto-recharge/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {})
        },
        body: JSON.stringify({ ruleId: rule.ruleId })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to test rule');
      }

      const data = await res.json();
      setTestResult(data);
    } catch (e) {
      setError(e.message || 'Failed to test rule');
    } finally {
      setTesting(false);
    }
  };

  const resetForm = () => {
    setRuleForm({
      accountId: accountId || '',
      ruleName: '',
      triggerType: 'THRESHOLD',
      thresholdAmount: '',
      thresholdPercentage: '',
      rechargeAmount: '',
      maxRechargePerMonth: '',
      scheduleFrequency: '',
      scheduleDay: '',
      paymentMethodId: ''
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      'ACTIVE': '#059669',
      'PAUSED': '#f59e0b',
      'SUSPENDED': '#dc2626',
      'DELETED': '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  const getTriggerTypeLabel = (type) => {
    const labels = {
      'THRESHOLD': 'Balance Threshold',
      'SCHEDULED': 'Scheduled',
      'DEPLETION': 'Depletion'
    };
    return labels[type] || type;
  };

  return (
    <div className="admin-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 className="admin-header">Auto-Recharge Rules</h2>
        <button
          onClick={() => {
            setEditingRule(null);
            resetForm();
            setShowRuleModal(true);
          }}
          style={{
            padding: '8px 16px',
            background: '#667eea',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 500
          }}
        >
          Create Rule
        </button>
      </div>

      {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}

      {/* Filters */}
      <div className="admin-controls" style={{ flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Status:</span>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              background: '#fff',
              cursor: 'pointer',
              minWidth: '150px'
            }}
          >
            <option value="">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="PAUSED">Paused</option>
            <option value="SUSPENDED">Suspended</option>
          </select>
        </label>
      </div>

      {/* Rules List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading...</div>
      ) : rules.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
          No auto-recharge rules found. Create one to get started.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {rules.map((rule) => (
            <div key={rule.ruleId} style={{ padding: 16, background: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{rule.ruleName}</h3>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: 12,
                      fontWeight: 500,
                      background: getStatusColor(rule.status) + '20',
                      color: getStatusColor(rule.status)
                    }}>
                      {rule.status}
                    </span>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, fontSize: 14 }}>
                    <div>
                      <span style={{ color: '#6b7280' }}>Trigger:</span>{' '}
                      <strong>{getTriggerTypeLabel(rule.triggerType)}</strong>
                    </div>
                    {rule.triggerType === 'THRESHOLD' && (
                      <>
                        {rule.thresholdAmount && (
                          <div>
                            <span style={{ color: '#6b7280' }}>Threshold:</span>{' '}
                            <strong>{formatCurrency(rule.thresholdAmount)}</strong>
                          </div>
                        )}
                        {rule.thresholdPercentage && (
                          <div>
                            <span style={{ color: '#6b7280' }}>Threshold:</span>{' '}
                            <strong>{rule.thresholdPercentage}%</strong>
                          </div>
                        )}
                      </>
                    )}
                    {rule.triggerType === 'SCHEDULED' && (
                      <>
                        <div>
                          <span style={{ color: '#6b7280' }}>Frequency:</span>{' '}
                          <strong>{rule.scheduleFrequency}</strong>
                        </div>
                        {rule.scheduleDay && (
                          <div>
                            <span style={{ color: '#6b7280' }}>Day:</span>{' '}
                            <strong>{rule.scheduleDay}</strong>
                          </div>
                        )}
                      </>
                    )}
                    <div>
                      <span style={{ color: '#6b7280' }}>Recharge Amount:</span>{' '}
                      <strong>{formatCurrency(rule.rechargeAmount)}</strong>
                    </div>
                    {rule.maxRechargePerMonth && (
                      <div>
                        <span style={{ color: '#6b7280' }}>Monthly Limit:</span>{' '}
                        <strong>{formatCurrency(rule.maxRechargePerMonth)}</strong>
                      </div>
                    )}
                    <div>
                      <span style={{ color: '#6b7280' }}>Times Triggered:</span>{' '}
                      <strong>{rule.timesTriggered}</strong>
                    </div>
                    {rule.lastTriggeredAt && (
                      <div>
                        <span style={{ color: '#6b7280' }}>Last Triggered:</span>{' '}
                        <strong>{formatDate(rule.lastTriggeredAt)}</strong>
                      </div>
                    )}
                    {rule.nextTriggerAt && (
                      <div>
                        <span style={{ color: '#6b7280' }}>Next Trigger:</span>{' '}
                        <strong>{formatDate(rule.nextTriggerAt)}</strong>
                      </div>
                    )}
                    {rule.account && (
                      <div>
                        <span style={{ color: '#6b7280' }}>Account:</span>{' '}
                        <strong>{rule.account.entityType} - {rule.account.entityId}</strong>
                        {rule.account.balance !== undefined && (
                          <span style={{ color: '#6b7280', marginLeft: 8 }}>
                            (Balance: {formatCurrency(rule.account.balance)})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => handleTestRule(rule)}
                    style={{
                      padding: '6px 12px',
                      background: '#3b82f6',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: 12
                    }}
                  >
                    Test
                  </button>
                  <button
                    onClick={() => {
                      setEditingRule(rule);
                      setRuleForm({
                        accountId: rule.accountId,
                        ruleName: rule.ruleName,
                        triggerType: rule.triggerType,
                        thresholdAmount: rule.thresholdAmount?.toString() || '',
                        thresholdPercentage: rule.thresholdPercentage?.toString() || '',
                        rechargeAmount: rule.rechargeAmount.toString(),
                        maxRechargePerMonth: rule.maxRechargePerMonth?.toString() || '',
                        scheduleFrequency: rule.scheduleFrequency || '',
                        scheduleDay: rule.scheduleDay?.toString() || '',
                        paymentMethodId: rule.paymentMethodId || '',
                        status: rule.status
                      });
                      setShowRuleModal(true);
                    }}
                    style={{
                      padding: '6px 12px',
                      background: 'transparent',
                      color: '#667eea',
                      border: '1px solid #667eea',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: 12
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteRule(rule.ruleId)}
                    style={{
                      padding: '6px 12px',
                      background: 'transparent',
                      color: '#dc2626',
                      border: '1px solid #dc2626',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: 12
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Rule Modal */}
      {showRuleModal && (
        <div className="modal-overlay" onClick={() => setShowRuleModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <button className="modal-close" onClick={() => setShowRuleModal(false)}>×</button>
            <h2 style={{ marginTop: 0, marginBottom: 20 }}>
              {editingRule ? 'Edit' : 'Create'} Auto-Recharge Rule
            </h2>

            <div style={{ display: 'grid', gap: 16 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>Rule Name *</span>
                <input
                  type="text"
                  value={ruleForm.ruleName}
                  onChange={e => setRuleForm({ ...ruleForm, ruleName: e.target.value })}
                  placeholder="e.g., Low Balance Alert"
                  style={{
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: '#fff'
                  }}
                />
              </label>

              {!editingRule && (
                <>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>Account ID *</span>
                    <input
                      type="text"
                      value={ruleForm.accountId}
                      onChange={e => setRuleForm({ ...ruleForm, accountId: e.target.value })}
                      placeholder="Account GUID"
                      style={{
                        padding: '10px 12px',
                        border: '1.5px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '14px',
                        background: '#fff'
                      }}
                    />
                  </label>

                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>Trigger Type *</span>
                    <select
                      value={ruleForm.triggerType}
                      onChange={e => setRuleForm({ ...ruleForm, triggerType: e.target.value })}
                      style={{
                        padding: '10px 12px',
                        border: '1.5px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '14px',
                        background: '#fff',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="THRESHOLD">Balance Threshold</option>
                      <option value="SCHEDULED">Scheduled</option>
                      <option value="DEPLETION">Depletion</option>
                    </select>
                  </label>

                  {ruleForm.triggerType === 'THRESHOLD' && (
                    <>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 500 }}>Threshold Amount ($)</span>
                        <input
                          type="number"
                          step="0.01"
                          value={ruleForm.thresholdAmount}
                          onChange={e => setRuleForm({ ...ruleForm, thresholdAmount: e.target.value, thresholdPercentage: '' })}
                          placeholder="e.g., 100.00"
                          style={{
                            padding: '10px 12px',
                            border: '1.5px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px',
                            background: '#fff'
                          }}
                        />
                      </label>
                      <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 12 }}>OR</div>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 500 }}>Threshold Percentage (%)</span>
                        <input
                          type="number"
                          step="0.01"
                          value={ruleForm.thresholdPercentage}
                          onChange={e => setRuleForm({ ...ruleForm, thresholdPercentage: e.target.value, thresholdAmount: '' })}
                          placeholder="e.g., 20.00"
                          style={{
                            padding: '10px 12px',
                            border: '1.5px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px',
                            background: '#fff'
                          }}
                        />
                      </label>
                    </>
                  )}

                  {ruleForm.triggerType === 'SCHEDULED' && (
                    <>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 500 }}>Schedule Frequency *</span>
                        <select
                          value={ruleForm.scheduleFrequency}
                          onChange={e => setRuleForm({ ...ruleForm, scheduleFrequency: e.target.value })}
                          style={{
                            padding: '10px 12px',
                            border: '1.5px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px',
                            background: '#fff',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="">Select frequency</option>
                          <option value="DAILY">Daily</option>
                          <option value="WEEKLY">Weekly</option>
                          <option value="MONTHLY">Monthly</option>
                        </select>
                      </label>
                      {(ruleForm.scheduleFrequency === 'WEEKLY' || ruleForm.scheduleFrequency === 'MONTHLY') && (
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span style={{ fontSize: 14, fontWeight: 500 }}>Schedule Day</span>
                          <input
                            type="number"
                            min="1"
                            max={ruleForm.scheduleFrequency === 'WEEKLY' ? 7 : 31}
                            value={ruleForm.scheduleDay}
                            onChange={e => setRuleForm({ ...ruleForm, scheduleDay: e.target.value })}
                            placeholder={ruleForm.scheduleFrequency === 'WEEKLY' ? '1-7 (day of week)' : '1-31 (day of month)'}
                            style={{
                              padding: '10px 12px',
                              border: '1.5px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '14px',
                              background: '#fff'
                            }}
                          />
                        </label>
                      )}
                    </>
                  )}
                </>
              )}

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>Recharge Amount ($) *</span>
                <input
                  type="number"
                  step="0.01"
                  value={ruleForm.rechargeAmount}
                  onChange={e => setRuleForm({ ...ruleForm, rechargeAmount: e.target.value })}
                  placeholder="e.g., 500.00"
                  style={{
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: '#fff'
                  }}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>Max Recharge Per Month ($)</span>
                <input
                  type="number"
                  step="0.01"
                  value={ruleForm.maxRechargePerMonth}
                  onChange={e => setRuleForm({ ...ruleForm, maxRechargePerMonth: e.target.value })}
                  placeholder="Optional - leave empty for no limit"
                  style={{
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: '#fff'
                  }}
                />
              </label>

              {editingRule && (
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>Status</span>
                  <select
                    value={ruleForm.status || 'ACTIVE'}
                    onChange={e => setRuleForm({ ...ruleForm, status: e.target.value })}
                    style={{
                      padding: '10px 12px',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: '#fff',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="PAUSED">Paused</option>
                    <option value="SUSPENDED">Suspended</option>
                  </select>
                </label>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button
                  onClick={() => {
                    setShowRuleModal(false);
                    setEditingRule(null);
                    resetForm();
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#f3f4f6',
                    color: '#111827',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: 14
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRule}
                  disabled={saving}
                  style={{
                    padding: '10px 20px',
                    background: '#667eea',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    fontWeight: 500,
                    opacity: saving ? 0.5 : 1
                  }}
                >
                  {saving ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Rule Modal */}
      {showTestModal && testingRule && (
        <div className="modal-overlay" onClick={() => setShowTestModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <button className="modal-close" onClick={() => setShowTestModal(false)}>×</button>
            <h2 style={{ marginTop: 0, marginBottom: 20 }}>Test Auto-Recharge Rule</h2>

            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{testingRule.ruleName}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  Trigger: {getTriggerTypeLabel(testingRule.triggerType)}
                </div>
              </div>

              {testing ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#6b7280' }}>Testing rule...</div>
              ) : testResult ? (
                <div style={{ padding: 16, background: '#f9fafb', borderRadius: '8px' }}>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Current Balance</div>
                    <div style={{ fontSize: 18, fontWeight: 600 }}>{formatCurrency(testResult.current_balance)}</div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Recharge Amount</div>
                    <div style={{ fontSize: 18, fontWeight: 600 }}>{formatCurrency(testResult.recharge_amount)}</div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Should Trigger</div>
                    <div style={{
                      fontSize: 18,
                      fontWeight: 600,
                      color: testResult.should_trigger ? '#059669' : '#dc2626'
                    }}>
                      {testResult.should_trigger ? 'YES' : 'NO'}
                    </div>
                  </div>
                  {testResult.reason && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Reason</div>
                      <div style={{ fontSize: 14 }}>{testResult.reason}</div>
                    </div>
                  )}
                  {testResult.monthly_limit_reached && (
                    <div style={{ padding: 12, background: '#fef3c7', borderRadius: '6px', color: '#92400e' }}>
                      Monthly limit reached: {formatCurrency(testResult.monthly_limit)}
                    </div>
                  )}
                  {testResult.next_trigger_at && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Next Trigger At</div>
                      <div style={{ fontSize: 14 }}>{formatDate(testResult.next_trigger_at)}</div>
                    </div>
                  )}
                </div>
              ) : null}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button
                  onClick={() => setShowTestModal(false)}
                  style={{
                    padding: '10px 20px',
                    background: '#f3f4f6',
                    color: '#111827',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: 14
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoRechargeRules;

